'use client'

import {FC, useEffect, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Section} from '@/components/common/content/Section'
import {Text, TextVariant} from '@/components/common/content/Text'
import {ErrorPageLayout} from '@/components/error/ErrorPageLayout'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {getBlogOverviewPath} from '@/utils/blog/helpers'
import {BlogOverview} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'

import {BlogPreview} from './BlogPreview'
import {BlogPreviewListSkeleton} from './BlogPreviewSkeleton'

const LATEST_BLOGS_COUNT = 3

/*
 * The 404 for everything under /blog, whichever route group the URL resolved in.
 *
 * The latest articles are fetched from the browser. A not-found boundary is rendered into the
 * payload of every page beneath it, so a server-side list would travel with every article that
 * does exist - and the (static) boundary could not ask for one anyway, since it has no locale.
 * Nothing is lost: a page that calls `notFound()` reaches the browser as an empty shell either way.
 */
export const BlogNotFound: FC = () => {
  const {t} = useTranslation()
  const language = useLanguage()
  const [blogs, setBlogs] = useState<BlogOverview[] | null>(null)

  useEffect(() => {
    const loadLatestBlogs = async () => {
      try {
        const response = await fetch(routeTo.api.contentful.blog.latest(language))
        setBlogs(response.ok ? await response.json() : [])
      } catch {
        setBlogs([])
      }
    }
    void loadLatestBlogs()
  }, [language])

  return (
    <ErrorPageLayout statusCode={404}>
      {blogs?.length !== 0 && (
        <Section hasPaddingBottom>
          <div className="latest-blogs">
            <div className="title">
              <Text variant={TextVariant.h3} element="h2">
                {t('page.recent-blogs-title')}
              </Text>
            </div>
            {blogs ? (
              blogs.map((blog) => (
                <BlogPreview key={blog.slug} blog={blog} linkHref={getBlogOverviewPath(blog)} />
              ))
            ) : (
              <BlogPreviewListSkeleton rows={Array(LATEST_BLOGS_COUNT).fill(0)} />
            )}
            <div className="button-wrapper">
              <Link href={routeTo.blog.home()}>
                <Button variant={ButtonVariant.secondary} type="button">
                  {t('page.button-see-all-articles')}
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      )}

      <style jsx>{`
        .latest-blogs {
          max-width: 800px;
          margin: 0 auto;
        }

        .title {
          margin-bottom: ${theme.spacing[5]};
        }

        .button-wrapper {
          margin-top: ${theme.spacing[5]};
          text-align: center;
        }
      `}</style>
    </ErrorPageLayout>
  )
}
